import React, {useEffect, useMemo, useState} from 'react'
import {Modal} from 'react-bootstrap'
import {collection, doc, getDoc, onSnapshot, updateDoc, Unsubscribe} from 'firebase/firestore'
import {showErrorToast, showSuccessToast} from 'app/components/ShowToast'
import {useAuth} from 'app/modules/auth'
import db from 'services/firebase'
import {KTIcon} from '_metronic/helpers'

type Props = {
  id?: string
  isOpen: boolean
  onClose: () => void
}

const AddRiskControls: React.FC<Props> = ({id, isOpen, onClose}) => {
  const [risk, setRisk] = useState([] as any[])
  const [control, setControls] = useState<any[]>([])
  const {currentUser} = useAuth()
  const [activeFrameworks, setActiveFrameworks] = useState<any[]>([])
  const [showRecommendedOnly, setShowRecommendedOnly] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedControls, setSelectedControls] = useState<string[]>([])
  const [isLoading, setIsLoading] = useState(false)

  const getRisk = async () => {
    try {
      const tenantId = currentUser?.tenantId || ''
      const tenantRef = doc(db, 'tenants', tenantId)
      const PolicyRef = collection(tenantRef, 'risks')

      const unsubscribe = onSnapshot(PolicyRef, (snapshot) => {
        const risksData = snapshot.docs
          .map((doc) => ({
            ...doc.data(),
            id: doc.id,
            recommendedControlsId: doc.data().recommendedControlsId || [],
            added_controls: doc.data().added_controls || [],
          }))
          .filter((data) => data.id === id)
        setRisk(risksData)
      })

      return unsubscribe
    } catch (error) {
      console.error('Error getting risks:', error)
    }
  }

  const getActiveFrameworks = async () => {
    try {
      const tenantId = currentUser?.tenantId || ''
      const tenantRef = doc(db, 'tenants', tenantId)
      const testsCollectionRef = collection(tenantRef, 'active_frameworks')

      const unsubscribe = onSnapshot(testsCollectionRef, (snapshot) => {
        const frameworksData = snapshot.docs.map((doc) => ({
          ...doc.data(),
          id: doc.id,
          frameworks: doc.data().frameworks,
        }))
        const frameworks = frameworksData[0]?.frameworks || []
        setActiveFrameworks(frameworks)
      })

      return unsubscribe
    } catch (error) {
      console.error('Error getting frameworks:', error)
    }
  }

  const getControl = async () => {
    try {
      const tenantId = currentUser?.tenantId || ''
      const tenantRef = doc(db, 'tenants', tenantId)
      const testsCollectionRef = collection(tenantRef, 'controls')

      const unsubscribe = onSnapshot(testsCollectionRef, (snapshot) => {
        const controlsData = snapshot.docs.map((doc) => ({
          ...doc.data(),
          id: doc.id,
          framework: doc.data().control_framework_mappings,
          status: doc.data().control_evidence_status,
        }))

        const filteredControls = controlsData.filter((control) => {
          if (!control.framework) return false
          return activeFrameworks.some(
            (framework) =>
              control.framework.startsWith(framework) &&
              !control.framework.startsWith('ISO 27001:2013')
          )
        })

        setControls(filteredControls)
      })

      return unsubscribe
    } catch (error) {
      console.error('Error getting controls:', error)
    }
  }

  useEffect(() => {
    let unsubscribe: Unsubscribe | undefined

    const fetchFrameworks = async () => {
      unsubscribe = await getActiveFrameworks()
    }

    fetchFrameworks()

    return () => {
      if (unsubscribe) {
        unsubscribe()
      }
    }
  }, [])

  useEffect(() => {
    let unsubscribe: Unsubscribe | undefined

    const fetchRisk = async () => {
      unsubscribe = await getRisk()
    }

    fetchRisk()

    return () => {
      if (unsubscribe) {
        unsubscribe()
      }
    }
  }, [id, showRecommendedOnly])

  useEffect(() => {
    let unsubscribe: Unsubscribe | undefined

    const fetchControls = async () => {
      unsubscribe = await getControl()
    }

    fetchControls()

    return () => {
      if (unsubscribe) {
        unsubscribe()
      }
    }
  }, [activeFrameworks])

  const handleToggleRecommended = () => {
    setShowRecommendedOnly(!showRecommendedOnly)
  }

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value)
  }

  const handleSelectControl = (controlId: string) => {
    setSelectedControls((prev) =>
      prev.includes(controlId) ? prev.filter((id) => id !== controlId) : [...prev, controlId]
    )
  }

  const filteredControls = useMemo(() => {
    return control.filter((item) => {
      const matchesSearch = item.control_title?.toLowerCase().includes(searchTerm.toLowerCase())
      const isRecommended = risk[0]?.recommendedControlsId?.includes(item.id) || false
      return showRecommendedOnly ? matchesSearch && isRecommended : matchesSearch
    })
  }, [control, searchTerm, showRecommendedOnly, risk])

  const addSelectedControls = async () => {
    try {
      setIsLoading(true)
      const tenantId = currentUser?.tenantId || ''
      if (id) {
        const riskRef = doc(db, 'tenants', tenantId, 'risks', id)
        const addedControls: {
          id: string
          control_title: string
          control_framework_mappings: string
        }[] = []

        for (const controlId of selectedControls) {
          const controlRef = doc(db, 'tenants', tenantId, 'controls', controlId)
          const controlDoc = await getDoc(controlRef)
          const currentRiskScenarios = controlDoc.exists()
            ? controlDoc.data().risk_scenario || []
            : []

          const selectedControl = control.find((ctrl) => ctrl.id === controlId)

          if (selectedControl) {
            const newRiskScenario = {
              riskSource: risk[0]?.riskSource || '',
              riskId: id,
              isRecommended: risk[0]?.recommendedControlsId?.includes(selectedControl.id) || false,
              status: risk[0]?.risk_status || '',
            }

            const riskScenarioExists = currentRiskScenarios.some(
              (scenario: any) => scenario.riskId === id
            )

            if (!riskScenarioExists) {
              await updateDoc(controlRef, {
                risk_scenario: [...currentRiskScenarios, newRiskScenario],
              })

              addedControls.push({
                id: controlId,
                control_title: selectedControl.control_title,
                control_framework_mappings: selectedControl.control_framework_mappings || '',
              })
            }
          }
        }

        if (addedControls.length > 0) {
          const riskDoc = await getDoc(riskRef)
          const currentAddedControls = riskDoc.exists() ? riskDoc.data().added_controls || [] : []

          await updateDoc(riskRef, {
            added_controls: [...currentAddedControls, ...addedControls],
          })
        }

        showSuccessToast({message: 'Controls added successfully'})
      }
      setSelectedControls([])
      onClose()
    } catch (error) {
      showErrorToast({message: 'Failed to add controls. Try again.'})
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Modal
      show={isOpen}
      onHide={onClose}
      backdropClassName='bg-opacity-1'
      centered
      size='lg'
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
      }}
      dialogClassName='modal-90w'
      onClick={(e) => e.stopPropagation()}
    >
      <Modal.Header
        closeButton
        style={{borderTopLeftRadius: '0.475rem', borderTopRightRadius: '0.475rem'}}
      >
        <Modal.Title>Add Controls</Modal.Title>
      </Modal.Header>
      <Modal.Body
        className='p-0 mt-4'
        style={{
          display: 'flex',
          flexDirection: 'column',
          height: 'calc(80vh - 60px)',
          borderBottomLeftRadius: '0.475rem',
          borderBottomRightRadius: '0.475rem',
          overflow: 'hidden',
        }}
      >
        <div className='static-content'>
          <div className='d-flex align-items-center position-relative my-1 px-6'>
            <KTIcon iconName='magnifier' className='fs-icon-1 position-absolute ms-6' />
            <input
              type='text'
              data-filter='search'
              className='form-control ps-14'
              placeholder='Search...'
              value={searchTerm}
              onChange={handleSearch}
            />
          </div>
          <div className='form-check form-switch form-switch-sm form-check-custom form-check-solid my-4 px-6 mb-8'>
            <input
              className='form-check-input'
              type='checkbox'
              checked={showRecommendedOnly}
              onChange={handleToggleRecommended}
            />
            <span className='fs-7 mx-2 fw-semibold ms-2'>Show recommended only</span>
          </div>
        </div>
        <div className='table-container' style={{overflowY: 'auto', flexGrow: 1}}>
          <table className='table table-hover table-rounded gy-7 gs-7 m-0'>
            <thead>
              <tr className='fs-6 fw-bold text-gray-600 border-bottom-2 border-top-2 border-gray-200'>
                <th className='min-w-50px'></th>
                <th className='min-w-500px'>CONTROLS</th>
                <th className='min-w-100px'>MAPPED FRAMEWORKS</th>
              </tr>
            </thead>
            <tbody>
              {filteredControls && filteredControls.length > 0 ? (
                filteredControls.map((item) => {
                  const isAdded = risk[0]?.added_controls?.some(
                    (addedControl: any) => addedControl.id === item.id
                  )

                  return (
                    <tr key={item.id} className='px-6'>
                      <td>
                        <div className='form-check form-check-sm form-check-custom form-check-solid'>
                          <input
                            className='form-check-input widget-9-check'
                            type='checkbox'
                            value='1'
                            checked={selectedControls.includes(item.id)}
                            onChange={() => handleSelectControl(item.id)}
                            disabled={isAdded}
                          />
                        </div>
                      </td>
                      <td>
                        <div
                          className={`fs-6 fw-bold ${!isAdded ? 'text-gray-900' : 'text-gray-500'}`}
                        >
                          {item.control_title}{' '}
                          {risk[0]?.recommendedControlsId?.includes(item.id) && (
                            <span className='badge badge-light-success ms-2'>Recommended</span>
                          )}
                          {isAdded && (
                            <span className='badge badge-light-secondary text-gray-600 ms-2'>
                              Added
                            </span>
                          )}
                        </div>
                      </td>
                      <td>{item.control_framework_mappings}</td>
                    </tr>
                  )
                })
              ) : (
                <tr>
                  <td colSpan={3} style={{height: '300px', border: 'none'}}>
                    <div
                      className='d-flex justify-content-center align-items-center text-center'
                      style={{height: '100%'}}
                    >
                      <div>
                        <img
                          src='/path/to/empty-state.png'
                          alt=''
                          className='img-fluid'
                          style={{maxWidth: '100px'}}
                        />
                        <div className='fs-6'>
                          <p style={{fontWeight: 'bold'}}>No controls found!</p>
                        </div>
                        <p className='fs-7 ms-2'>
                          We couldn't find any controls that match your search. Try a different
                          search!
                        </p>
                      </div>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className='modal-footer p-0 my-4 pt-4 px-6'>
          <button
            className='btn btn-sm btn-light fs-ms ms-2'
            onClick={onClose}
            style={{
              width: '80px',
              height: '40px',
              borderRadius: '4px',
              transition: 'background-color 0.3s',
            }}
          >
            Cancel
          </button>
          {isLoading ? (
            <button
              className='btn ms-2 btn-sm fs-6'
              type='submit'
              style={{
                backgroundColor: '#0E9F6E',
                color: '#fff',
                width: '190px',
                height: '40px',
                borderRadius: '4px',
                transition: 'border-color 0.3s',
              }}
              disabled
            >
              Adding Controls...{' '}
              <span className='ms-spinner-border spinner-border-sm align-middle ms-2'></span>
            </button>
          ) : (
            <button
              className='ms-2 btn btn-sm fs-6'
              type='submit'
              onClick={addSelectedControls}
              onMouseEnter={(e: React.MouseEvent<HTMLElement>) =>
                (e.currentTarget.style.backgroundColor = '#148363')
              }
              onMouseLeave={(e: React.MouseEvent<HTMLElement>) =>
                (e.currentTarget.style.backgroundColor = '#0E9F6E')
              }
              style={{
                backgroundColor: '#0E9F6E',
                color: '#fff',
                width: '190px',
                height: '40px',
                borderRadius: '4px',
                transition: 'background-color 0.3s',
              }}
            >
              Add Selected Controls
            </button>
          )}
        </div>
      </Modal.Body>
    </Modal>
  )
}

export default AddRiskControls
